import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"
import { newUploadState, uploadMultipart } from "@/lib/s3_multipart_upload"

Dropzone.autoDiscover = false

// Single-file dropzone for large QuickBooks backups. Stages the file, then
// uploads it straight to S3 in parts when the document portal calls upload().
// A failed upload keeps its S3 session so the next upload() resumes it; the
// session is only aborted when the prospect removes or replaces the file.
export default class extends Controller {
  static targets = ["dropzone", "fileList", "progress", "progressBar", "status", "percent", "error"]

  static values = {
    sessionUrl: String,
    partsUrl: String,
    directToS3: { type: Boolean, default: false },
    maxFileSize: { type: Number, default: 20 * 1024 * 1024 * 1024 },
    acceptedFiles: { type: String, default: ".qbb,.QBB" },
    documentKind: { type: String, default: "qbd_backup" }
  }

  connect() {
    this.stagedFile = null
    this.uploadState = newUploadState()
    this.registered = false
    this.uploading = false
    this.percent = 0
    this.abortController = null
    this.warnBeforeUnload = (event) => event.preventDefault()

    // maxFiles stays unset: Dropzone would reject a replacement before accept()
    // runs, so accept() swaps the staged file itself once the new one is valid.
    this.dropzone = new Dropzone(this.dropzoneTarget, {
      url: "/",
      autoProcessQueue: false,
      maxFilesize: this.maxFileSizeValue / (1024 * 1024),
      acceptedFiles: this.acceptedFilesValue,
      createImageThumbnails: false,
      previewsContainer: false,
      dictInvalidFileType: "Please choose a QuickBooks backup (.qbb) file.",
      dictFileTooBig: `That file is too large. The maximum size is ${formatBytes(this.maxFileSizeValue)}.`,
      accept: (file, done) => this.acceptFile(file, done)
    })

    this.dropzone.on("error", (file, message) => this.rejectFile(file, message))
    this.dropzone.on("removedfile", () => this.renderFile())
    this.dropzone.on("dragenter", () => this.dropzoneTarget.classList.add("dragover"))
    this.dropzone.on("dragleave", () => this.dropzoneTarget.classList.remove("dragover"))
    this.dropzone.on("drop", () => this.dropzoneTarget.classList.remove("dragover"))
  }

  disconnect() {
    this.abortController?.abort()
    if (!this.registered) this.abortSession(this.uploadState)
    this.dropzone?.destroy()
    window.removeEventListener("beforeunload", this.warnBeforeUnload)
  }

  async upload() {
    const file = this.stagedFile
    if (!file) return []
    if (this.registered) return [{ uploadId: null, file, registered: true }]
    if (!this.directToS3Value) return [{ uploadId: null, file }]

    return [{ uploadId: await this.uploadToS3(file), file }]
  }

  markRegistered() {
    this.registered = true
    this.uploadState = newUploadState()
    this.progressTarget.classList.remove("d-none")
    this.setPercent(100)
    this.statusTarget.textContent = "Uploaded."
    this.renderFile()
  }

  // The server no longer knows this upload, so the next upload() starts over.
  discardUpload() {
    this.uploadState = newUploadState()
    this.statusTarget.textContent = "Your upload expired. Click Submit documents to upload it again."
  }

  removeFile(event) {
    event.preventDefault()
    if (this.uploading || this.registered || !this.stagedFile) return

    const file = this.stagedFile
    this.unstage()
    this.dropzone.removeFile(file)
  }

  acceptFile(file, done) {
    if (this.uploading) {
      done("Please wait for the current upload to finish.")
      return
    }

    const previous = this.stagedFile
    if (previous) this.unstage()
    this.stagedFile = file
    done()

    if (previous) this.dropzone.removeFile(previous)
    this.clearError()
    this.renderFile()
  }

  rejectFile(file, message) {
    if (file !== this.stagedFile) this.dropzone.removeFile(file)
    this.showError(typeof message === "string" ? message : "That file could not be added.")
  }

  unstage() {
    if (!this.registered) this.abortSession(this.uploadState)
    this.stagedFile = null
    this.uploadState = newUploadState()
    this.registered = false
    this.percent = 0
    this.progressTarget.classList.add("d-none")
    this.setPercent(0)
  }

  async uploadToS3(file) {
    this.abortController = new AbortController()
    this.startProgress()

    try {
      const uploadId = await uploadMultipart(file, this.uploadState, {
        sessionUrl: this.sessionUrlValue,
        partsUrl: this.partsUrlValue,
        signal: this.abortController.signal,
        documentKind: this.documentKindValue,
        onProgress: (sent, total) => this.updateProgress(sent, total),
        onFinishing: () => { this.statusTarget.textContent = "Finalizing upload..." }
      })

      this.stopProgress()
      this.statusTarget.textContent = "Upload complete."
      return uploadId
    } catch (error) {
      this.stopProgress()
      if (error.name !== "AbortError") {
        this.statusTarget.textContent = `Upload interrupted at ${this.percent}%. Click Submit documents to resume.`
      }
      throw error
    } finally {
      this.abortController = null
    }
  }

  abortSession(state) {
    if (!this.directToS3Value || !state.uploadId) return

    fetch(this.sessionUrlValue, {
      method: "DELETE",
      keepalive: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({ upload_id: state.uploadId })
    }).catch(() => {})
  }

  renderFile() {
    const file = this.stagedFile
    this.fileListTarget.replaceChildren()
    this.fileListTarget.classList.toggle("d-none", !file)
    this.dropzoneTarget.classList.toggle("sales-document-portal__dropzone--compact", Boolean(file))
    if (!file) return

    const item = document.createElement("li")
    item.className = "sales-document-portal__staged-item"

    const icon = document.createElement("span")
    icon.className = "sales-document-portal__staged-icon"
    icon.innerHTML = '<i class="fa-solid fa-file text-muted" aria-hidden="true"></i>'

    const name = document.createElement("span")
    name.className = "sales-document-portal__staged-name"
    name.textContent = file.name
    name.title = file.name

    const size = document.createElement("span")
    size.className = "sales-document-portal__staged-size"
    size.textContent = formatBytes(file.size)

    item.append(icon, name, size)

    if (!this.uploading && !this.registered) {
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "sales-document-portal__staged-remove btn btn-sm btn-link"
      remove.dataset.action = "click->multipart-dropzone#removeFile"
      remove.setAttribute("aria-label", `Remove ${file.name}`)
      remove.innerHTML = '<i class="fa-regular fa-xmark" aria-hidden="true"></i>'
      item.append(remove)
    }

    this.fileListTarget.append(item)
  }

  startProgress() {
    this.uploading = true
    window.addEventListener("beforeunload", this.warnBeforeUnload)
    this.progressTarget.classList.remove("d-none")
    this.renderFile()
  }

  stopProgress() {
    this.uploading = false
    window.removeEventListener("beforeunload", this.warnBeforeUnload)
    this.renderFile()
  }

  updateProgress(sent, total) {
    this.setPercent(Math.min(100, Math.floor((sent / total) * 100)))
    this.statusTarget.textContent = `Uploading ${formatBytes(sent)} of ${formatBytes(total)}. Please keep this page open.`
  }

  setPercent(percent) {
    this.percent = percent
    this.percentTarget.textContent = `${percent}%`
    this.progressBarTarget.style.width = `${percent}%`
    this.progressBarTarget.parentElement.setAttribute("aria-valuenow", percent)
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`

  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}
